/**
 * Turning a CLI transcript chunk into readable activity lines.
 *
 * The runner streams each agent CLI's raw output verbatim, which is JSON: Claude in
 * `stream-json` events, Codex in `--json` JSONL. Rendering that raw in a log tail shows the
 * operator a wall of `{"type":"assistant","message":{...}}` and hides the only thing they
 * want to know — what the agent is doing right now. So the dashboard's tail is a *reading*
 * of the stream: one short line per meaningful act (a command run, a file written, a
 * sentence said), which is what the design brief's real examples look like
 * (`Wrote gridguard/render.py`, `$ python -m pytest -q ... 425 passed`, `Pushed c36bf795`).
 *
 * Rules that keep it honest:
 *  - Anything unrecognised passes through as-is (trimmed). A future CLI event shape
 *    degrades to raw text rather than to silence, and silence in a log tail reads as a
 *    stalled agent.
 *  - Nothing is invented or reordered. The full stream is always one click away in the
 *    drawer's Raw tab; this is a summary, not a replacement.
 */

const MAX_LINE = 160;

/**
 * How far a line may run before it is cut. The live tail clips hard (a narrow column, a
 * fresh line every second); a viewer showing a finished run wants every character, so it
 * passes `Infinity` and only whitespace is flattened.
 */
export type LineOptions = { maxLine?: number };

function clip(text: string, max = MAX_LINE): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
}

/** Clip to the tighter of a local budget and the caller's ceiling; no ceiling means no clipping. */
function within(limit: number, local: number = MAX_LINE): number {
  return Number.isFinite(limit) ? Math.min(limit, local) : Infinity;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Shorten a path to its last two segments — full paths swamp a narrow tail. */
function shortPath(value: string): string {
  const parts = value.replace(/\\/g, '/').split('/').filter(Boolean);
  return parts.length <= 2 ? parts.join('/') : parts.slice(-2).join('/');
}

function toolLine(
  name: string,
  input: Record<string, unknown> | undefined,
  limit: number,
): string | undefined {
  const path = asString(input?.file_path) ?? asString(input?.path) ?? asString(input?.notebook_path);
  switch (name) {
    case 'Bash':
    case 'BashOutput': {
      const command = asString(input?.command);
      return command ? `$ ${clip(command, within(limit, 120))}` : undefined;
    }
    case 'Write':
      return path ? `Wrote ${shortPath(path)}` : 'Wrote a file';
    case 'Edit':
    case 'MultiEdit':
    case 'NotebookEdit':
      return path ? `Edited ${shortPath(path)}` : 'Edited a file';
    case 'Read':
      return path ? `Read ${shortPath(path)}` : 'Read a file';
    case 'Glob':
      return `Globbed ${clip(asString(input?.pattern) ?? '', within(limit, 60))}`;
    case 'Grep':
      return `Searched ${clip(asString(input?.pattern) ?? '', within(limit, 60))}`;
    case 'TodoWrite':
      return 'Updated its plan';
    case 'WebFetch':
    case 'WebSearch':
      return `Looked up ${clip(asString(input?.url) ?? asString(input?.query) ?? '', within(limit, 60))}`;
    default: {
      const detail = path ? ` ${shortPath(path)}` : '';
      return `${name}${detail}`;
    }
  }
}

function claudeEventLines(event: Record<string, unknown>, limit: number): string[] {
  const out: string[] = [];
  const type = asString(event.type);

  if (type === 'system') {
    const subtype = asString(event.subtype);
    if (subtype === 'init') {
      const model = asString(event.model);
      out.push(model ? `session started · ${model}` : 'session started');
    }
    return out;
  }

  if (type === 'assistant' || type === 'user') {
    const message = asRecord(event.message);
    const content = Array.isArray(message?.content) ? message.content : [];
    for (const raw of content) {
      const block = asRecord(raw);
      if (!block) continue;
      const blockType = asString(block.type);
      if (blockType === 'text') {
        const text = asString(block.text);
        if (text) out.push(clip(text, within(limit)));
      } else if (blockType === 'tool_use') {
        const line = toolLine(asString(block.name) ?? 'tool', asRecord(block.input), limit);
        if (line) out.push(line);
      } else if (blockType === 'tool_result' && block.is_error === true) {
        const body = typeof block.content === 'string' ? block.content : JSON.stringify(block.content);
        out.push(`! ${clip(firstLine(body), within(limit, 120))}`);
      }
    }
    return out;
  }

  if (type === 'rate_limit_event') {
    const info = asRecord(event.rate_limit_info);
    if (info?.status === 'rejected') out.push('! vendor rate limit — waiting for the window to reset');
    return out;
  }

  if (type === 'result') {
    const status = event.is_error === true ? 'failed' : 'finished';
    out.push(`run ${status}`);
    return out;
  }

  return out;
}

function codexEventLines(event: Record<string, unknown>, limit: number): string[] {
  const type = asString(event.type);
  if (type === 'thread.started') return ['session started'];
  if (type === 'turn.completed') {
    const usage = asRecord(event.usage);
    const output = typeof usage?.output_tokens === 'number' ? usage.output_tokens : undefined;
    return [output === undefined ? 'turn completed' : `turn completed · ${output} output tokens`];
  }
  if (type !== 'item.completed' && type !== 'item.started') return [];

  const item = asRecord(event.item);
  if (!item) return [];
  const itemType = asString(item.type);

  if (itemType === 'agent_message' || itemType === 'assistant_message') {
    const text = asString(item.text);
    return text ? [clip(text, within(limit))] : [];
  }
  if (itemType === 'command_execution') {
    const command = asString(item.command);
    const exit = typeof item.exit_code === 'number' ? item.exit_code : undefined;
    const lines: string[] = [];
    if (command) lines.push(`$ ${clip(command, within(limit, 120))}`);
    if (exit !== undefined && exit !== 0) lines.push(`! exited ${exit}`);
    return lines;
  }
  if (itemType === 'file_change' || itemType === 'patch_apply') {
    const changes = Array.isArray(item.changes) ? item.changes : [];
    const paths = changes
      .map((change) => asString(asRecord(change)?.path))
      .filter((value): value is string => value !== undefined);
    if (paths.length === 0) return ['Applied a patch'];
    return [`Wrote ${paths.map(shortPath).join(', ')}`];
  }
  if (itemType === 'error') {
    const message = asString(item.message);
    return [`! ${clip(message ?? 'error', within(limit, 120))}`];
  }
  if (itemType === 'reasoning' || itemType === 'todo_list') return [];

  return [];
}

function firstLine(text: string): string {
  const nl = text.indexOf('\n');
  return nl === -1 ? text : text.slice(0, nl);
}

/**
 * Extract readable lines from one streamed transcript chunk.
 *
 * A chunk is not guaranteed to end on a line boundary, so the caller keeps the trailing
 * partial line and prepends it next time. That is why this returns the leftover too:
 * splitting naively would mangle roughly one line per HTTP chunk, and the mangled one is
 * usually the newest — the one on screen.
 */
export function extractLogLines(
  chunk: string,
  carry = '',
  options: LineOptions = {},
): { lines: string[]; carry: string } {
  const limit = options.maxLine ?? MAX_LINE;
  const text = carry + chunk;
  const parts = text.split(/\r?\n/);
  const leftover = parts.pop() ?? '';
  const lines: string[] = [];

  for (const raw of parts) {
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;
    if (!trimmed.startsWith('{')) {
      lines.push(clip(trimmed, within(limit)));
      continue;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      lines.push(clip(trimmed, within(limit)));
      continue;
    }
    const event = asRecord(parsed);
    if (!event) {
      lines.push(clip(trimmed, within(limit)));
      continue;
    }
    const type = asString(event.type) ?? '';
    const produced =
      type.includes('.') || type === 'item' ? codexEventLines(event, limit) : claudeEventLines(event, limit);
    // A Codex-shaped event has a dotted type; Claude's are bare words. When neither
    // reading produces anything the event is real but uninteresting (reasoning, deltas):
    // drop it rather than emit noise.
    lines.push(...produced);
  }

  // A partial line can be arbitrarily long (a tool result holding a whole file). Cap the
  // carry so a pathological chunk cannot grow the buffer without bound.
  const carryOut = leftover.length > 256 * 1024 ? '' : leftover;
  return { lines, carry: carryOut };
}

/**
 * Read a whole slice of transcript at once.
 *
 * Same extraction as the live path, minus the carry bookkeeping — used by the drawer's
 * transcript viewer so a historical run and a running one are summarised identically. If they
 * disagreed, the readable view of a finished run would not match what the operator watched
 * scroll past while it ran, which is exactly when a difference is most confusing.
 */
export function readableTranscriptLines(text: string, options: LineOptions = {}): string[] {
  // A trailing newline is appended so the final line is not held back as a carry: the caller
  // has the whole slice, and dropping its last line would silently lose the most recent act.
  const terminated = text.endsWith('\n') ? text : `${text}\n`;
  return extractLogLines(terminated, '', options).lines;
}
